/**
 * Immobilien – Geschäfte
 * /immobilien/geschaefte
 *
 * Geldwäsche-Betriebe: kaufen, upgraden, verkaufen, Wäsche starten.
 * Design: Terminal-UI, Share Tech Mono, keine Emojis, keine Gradienten.
 * Mobile: Bottom-Sheet Modals, min 44px Touch-Targets, full-width Buttons.
 */

import { api, ApiError } from '../../services/api.js';
import { gameState } from '../../state/gameState.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { MOBILE_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

// ─── Typen (API-Response) ─────────────────────────────────────────────────────

interface LaundryRunState {
  id: string;
  businessId: string;
  blackMoneyAmount: string;
  noxOutput: string;
  lossPercent: number;
  status: 'RUNNING' | 'COMPLETED' | 'INTERRUPTED';
  startedAt: string;
  completesAt: string;
  completedAt: string | null;
  progressPercent: number;
}

interface LaundryBusinessState {
  id: string;
  type: string;
  name: string;
  description: string;
  upgradeLevel: number;
  maxUpgradeLevel: number;
  status: 'ACTIVE' | 'CREDIT' | 'FROZEN';
  creditSince: string | null;
  maintenanceDebt: string;
  maintenanceDebtDisplay: string;
  maxDebt: string;
  hoursUntilFreeze: number | null;
  nextMaintenanceDueAt: string;
  effective: { maxBatchCents: string; lossPercent: number; durationMinutes: number };
  upgrade: { available: boolean; costEurCents: string | null };
  sale: { valueEurCents: string; hasActiveRun: boolean };
  maintenance: { perWeekCents: string; canPayWithBlackMoney: boolean; eurAlternativeCents: string };
  activeRun: LaundryRunState | null;
}

interface DebtSummaryItem {
  businessId: string;
  businessName: string;
  status: 'ACTIVE' | 'CREDIT' | 'FROZEN';
  maintenanceDebtCents: string;
  maintenanceDebtDisplay: string;
  weeksInDebt: number;
  hoursUntilFreeze: number | null;
  canPayWithBlackMoney: boolean;
  eurAlternativeCents: string;
}

interface LaundryBusinessDef {
  type: string;
  name: string;
  description: string;
  purchasePriceEurCents: string;
  maintenancePerWeekCents: string;
  maxBatchCents: string;
  baseLossPercent: number;
  baseDurationMinutes: number;
  maxUpgradeLevel: number;
  unlockLevel: number;
  basePoliceRiskPercent: number;
}

// ─── Modul-State ──────────────────────────────────────────────────────────────

let businesses: LaundryBusinessState[] = [];
let debtSummary: DebtSummaryItem[] = [];
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let progressTimer: ReturnType<typeof setInterval> | null = null;

// ─── Format-Helfer ────────────────────────────────────────────────────────────

function fmtSeur(cents: string): string {
  const val = Math.round(parseInt(cents, 10) / 100);
  return new Intl.NumberFormat('de-DE').format(val) + ' S-₦';
}

function fmtEur(cents: string): string {
  const val = Math.round(parseInt(cents, 10) / 100);
  return new Intl.NumberFormat('de-DE').format(val) + ' ₦';
}

function fmtDuration(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} Min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${String(h)}h ${String(m)}m` : `${String(h)}h`;
}

function calcProgress(run: LaundryRunState): number {
  const now = Date.now();
  const start = new Date(run.startedAt).getTime();
  const end = new Date(run.completesAt).getTime();
  const total = end - start;
  if (total <= 0) return 100;
  return Math.min(100, Math.floor(((now - start) / total) * 100));
}

function timeLeft(completesAt: string): string {
  const ms = new Date(completesAt).getTime() - Date.now();
  if (ms <= 0) return 'FERTIG';
  const totalSec = Math.ceil(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  if (m > 0) return `${String(m)}m ${String(s)}s`;
  return `${String(s)}s`;
}

// ─── API-Calls ────────────────────────────────────────────────────────────────

async function loadData(): Promise<void> {
  try {
    const result = await api.get<{ businesses: LaundryBusinessState[]; debtSummary: DebtSummaryItem[] }>(
      '/laundry/businesses',
    );
    businesses = result.businesses;
    debtSummary = result.debtSummary;
    renderFull();
  } catch (err) {
    showError(err instanceof ApiError ? err.message : 'Verbindungsfehler.');
  }
}

// ─── Dashboard-Tab-Integration ────────────────────────────────────────────────

/**
 * HTML-String für den Dashboard-Tab (wird von dashboard.ts in den switch eingesetzt).
 */
export function renderImmobilienTab(): string {
  return `
    <section class="dash-content" id="immo-root" style="padding:0;">
      <div class="prop-page-header">
        <div>
          <h2 class="prop-page-title">GESCHÄFTE</h2>
          <p class="prop-page-subtitle">Geldwäsche-Betriebe kaufen, upgraden und verwalten.</p>
        </div>
        <span class="immo-bm-balance" id="immo-bm-display">
          ${(parseInt(gameState.getPlayer()?.blackMoney ?? '0', 10) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} S-₦
        </span>
      </div>
      <div id="immo-content" style="padding:var(--space-3) var(--space-4);">
        <p style="font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);text-align:center;padding:var(--space-8) 0;">LADE...</p>
      </div>
    </section>
  `;
}

/**
 * Immobilien-Tab mounten: Styles, Daten laden, Timer starten.
 * Wird von dashboard.ts nach renderAll() aufgerufen.
 */
export function mountImmobilienTab(): void {
  addSubtabStyles();
  ensureModals();
  void loadData();
  progressTimer = setInterval(updateProgressBars, 5000);
  refreshTimer = setInterval(() => { void loadData(); }, 30_000);
}

/**
 * Cleanup beim Tab-Wechsel: Timer stoppen, Modals entfernen.
 */
export function cleanupImmobilien(): void {
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  if (progressTimer !== null) { clearInterval(progressTimer); progressTimer = null; }
  document.getElementById('immo-modal-overlay')?.remove();
  document.getElementById('immo-modal')?.remove();
}

/** Modal-Elemente in document.body sicherstellen. */
function ensureModals(): void {
  if (document.getElementById('immo-modal-overlay')) return;
  const overlay = document.createElement('div');
  overlay.id = 'immo-modal-overlay';
  overlay.className = 'modal-backdrop';
  overlay.style.display = 'none';
  overlay.innerHTML = '<div class="modal" id="immo-modal" role="dialog" aria-modal="true"></div>';
  document.body.appendChild(overlay);
}

function addSubtabStyles(): void {
  if (document.getElementById('immo-styles')) return;
  const style = document.createElement('style');
  style.id = 'immo-styles';
  style.textContent = `
    /* ── Fallback für prop-* Klassen (falls prop-styles noch nicht injiziert) ── */
    .prop-page-header {
      display:flex;align-items:flex-start;justify-content:space-between;
      padding:var(--space-3) var(--space-4) var(--space-2);border-bottom:1px solid var(--border);
      flex-wrap:wrap;gap:var(--space-2);
    }
    .prop-page-title {
      font-family:var(--font-mono);font-size:var(--font-size-base);letter-spacing:0.12em;
      color:var(--accent-gold);margin:0 0 4px;text-transform:uppercase;
    }
    @media(min-width:1024px){.prop-page-title{font-size:var(--font-size-xl)}}
    .prop-page-subtitle {
      font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0;line-height:1.4;
    }
    .prop-section-title {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.12em;
      color:var(--text-muted);text-transform:uppercase;
      border-bottom:1px solid var(--border);padding-bottom:var(--space-2);
      margin:0 0 var(--space-3);
    }

    /* ── S-₦ Balance ── */
    .immo-bm-balance {
      font-family:var(--font-mono);font-size:var(--font-size-sm);
      color:var(--accent-bm);letter-spacing:0.04em;
    }
    @media(min-width:1024px) {
      .immo-bm-balance { font-size:var(--font-size-lg); }
    }

    /* ── Buttons ── */
    .immo-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.06em;text-transform:uppercase;
      padding:var(--space-2) var(--space-3);background:transparent;border-radius:var(--radius-btn);cursor:pointer;
      min-height:40px;touch-action:manipulation;-webkit-tap-highlight-color:transparent;
    }
    .immo-btn-primary { border:1px solid var(--accent-green);color:var(--accent-green); }
    .immo-btn-warn    { border:1px solid var(--accent-orange);color:var(--accent-orange); }
    .immo-btn-danger  { border:1px solid var(--accent-red);color:var(--accent-red); }
    .immo-btn-muted   { border:1px solid var(--border);color:var(--text-muted); }
    .immo-btn:disabled { opacity:0.4;cursor:not-allowed; }
    @media ${MOBILE_LAYOUT_QUERY} {
      .immo-btn { width:100%; }
      .immo-btn-row { flex-direction:column !important; }
    }

    /* ── Fortschrittsbalken ── */
    .immo-progress-bar-track { background:var(--bg-elevated);border:1px solid var(--border);height:20px;position:relative;overflow:hidden; }
    .immo-progress-bar-fill  { height:100%;background:var(--accent-green);transition:width 1s linear; }

    .immo-row { display:flex;gap:var(--space-2);flex-wrap:wrap; }

    /* ── Betriebe-Management-Karte (gleiches Look&Feel wie prop-card) ── */
    .immo-biz-card {
      border:1px solid var(--border);border-radius:var(--radius);padding:var(--space-4);
      margin-bottom:var(--space-3);background:var(--bg-surface);
    }
    .immo-biz-header {
      display:flex;justify-content:space-between;align-items:baseline;
      border-bottom:1px solid var(--border);padding-bottom:var(--space-2);margin-bottom:var(--space-3);
      flex-wrap:wrap;gap:var(--space-2);
    }
    .immo-biz-name { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);letter-spacing:0.08em;text-transform:uppercase; }
    .immo-biz-status-badge { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.08em; }
    .immo-biz-meta { display:flex;gap:var(--space-4);flex-wrap:wrap;margin-bottom:var(--space-3); }
    .immo-biz-meta-item { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);line-height:1.5; }
    .immo-biz-debt-box {
      padding:var(--space-2) var(--space-3);margin-bottom:var(--space-3);
      border:1px solid; border-radius:var(--radius-sm);
    }
    .immo-biz-debt-label { font-family:var(--font-mono);font-size:var(--font-size-xs);margin:0 0 var(--space-2) 0; }

    /* ── Schulden-Banner ── */
    .immo-debt-banner {
      border:1px solid var(--accent-red);border-radius:var(--radius);padding:var(--space-3);
      margin-bottom:var(--space-4);background:color-mix(in srgb,var(--accent-red) 6%,transparent);
    }
    .immo-debt-banner-title {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.12em;
      color:var(--accent-red);text-transform:uppercase;margin:0 0 var(--space-3) 0;
    }
    .immo-debt-item { margin-bottom:var(--space-3);padding-bottom:var(--space-3);border-bottom:1px solid rgba(230,57,70,0.2); }
    .immo-debt-item:last-of-type { margin-bottom:0;border-bottom:none; }
    .immo-debt-item-row { display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px; }
    .immo-debt-item-name { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);letter-spacing:0.04em; }
    .immo-debt-item-status { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.08em; }
    .immo-debt-item-info { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0;line-height:1.5; }
    .immo-debt-footer { border-top:1px solid rgba(230,57,70,0.3);padding-top:var(--space-2);display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:var(--space-2); }
    .immo-debt-total { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }

    /* ── Shop-Liste (Betriebe kaufen – analog prop-catalog) ── */
    .immo-shop-section-title {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.12em;
      color:var(--text-muted);text-transform:uppercase;
      border-bottom:1px solid var(--border);padding-bottom:var(--space-2);
      margin:var(--space-4) 0 var(--space-3) 0;
    }
    .immo-shop-header {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.10em;
      color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-3) 0;
    }
    .immo-shop-item {
      display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;
      gap:var(--space-2);padding:var(--space-3) 0;border-bottom:1px solid var(--border);
    }
    .immo-shop-item:last-child { border-bottom:none; }
    .immo-shop-info { flex:1;min-width:160px; }
    .immo-shop-name { font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:0.08em;text-transform:uppercase;display:block;margin-bottom:var(--space-1); }
    .immo-shop-lore { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-italic);font-style:italic;display:block;margin-bottom:var(--space-2); }
    .immo-shop-stats { display:flex;gap:var(--space-3);flex-wrap:wrap; }
    .immo-shop-stat { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .immo-shop-actions { display:flex;align-items:center;gap:var(--space-2);flex-shrink:0; }
    .immo-shop-price { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-gold);letter-spacing:0.04em; }

    /* ── Modal-Inhalt (Shell = .modal-backdrop/.modal aus dashboard.css,
       siehe components/BottomSheet.ts) ── */
    .immo-modal-subtitle { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:var(--space-1) 0 0 0; }
    .immo-modal-label { font-family:var(--font-mono);font-size:11px;color:var(--text-muted);display:block;margin-bottom:var(--space-1); }
    .immo-modal-kv { display:flex;justify-content:space-between;margin-bottom:var(--space-1); }
    .immo-modal-kv-key { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .immo-modal-kv-val { font-family:var(--font-mono);font-size:var(--font-size-xs); }
    .immo-modal-amount { font-family:var(--font-mono);font-size:var(--font-size-base);color:var(--accent-bm);text-align:center;margin:var(--space-1) 0 var(--space-3) 0;display:block; }
    .immo-modal-preview { background:var(--bg-elevated);border:1px solid var(--border);border-radius:var(--radius-sm);padding:var(--space-3);margin-bottom:var(--space-3); }
    .immo-modal-warn { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-red);margin:var(--space-3) 0;line-height:1.5; }
  `;
  document.head.appendChild(style);
}

function showError(msg: string): void {
  const content = document.getElementById('immo-content');
  if (!content) return;
  content.innerHTML = `<p style="font-family:var(--font-mono);font-size:12px;color:var(--accent-red);text-align:center;padding:var(--space-8) 0;">${msg}</p>`;
}

// ─── Haupt-Content-Render ─────────────────────────────────────────────────────

function renderFull(): void {
  const content = document.getElementById('immo-content');
  if (!content) return;

  // BLACK_MONEY Display aktualisieren
  const bmDisplay = document.getElementById('immo-bm-display');
  if (bmDisplay) {
    const bm = parseInt(gameState.getPlayer()?.blackMoney ?? '0', 10) / 100;
    bmDisplay.textContent = bm.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' S-₦';
  }

  const hasDebt = debtSummary.some((d) => d.status !== 'ACTIVE');

  let html = '';
  if (hasDebt && debtSummary.length > 0) {
    html += renderDebtBanner();
  }
  html += renderBusinessShop();

  content.innerHTML = html;
  attachContentListeners();
}

// ─── Schulden-Banner ──────────────────────────────────────────────────────────

function renderDebtBanner(): string {
  const totalDebt = debtSummary.reduce((sum, d) => sum + parseInt(d.maintenanceDebtCents, 10), 0);
  const items = debtSummary.map((d) => `
    <div class="immo-debt-item">
      <div class="immo-debt-item-row">
        <span class="immo-debt-item-name">${d.businessName}</span>
        <span class="immo-debt-item-status" style="color:${d.status === 'FROZEN' ? 'var(--accent-red)' : 'var(--accent-orange)'};">
          ${d.status === 'FROZEN' ? 'EINGEFROREN' : 'AUF KREDIT'}
        </span>
      </div>
      <p class="immo-debt-item-info">
        Schulden: ${d.maintenanceDebtDisplay}
        ${d.hoursUntilFreeze !== null
          ? `· Einfrieren in: ${String(d.hoursUntilFreeze)}h`
          : '· Maximale Schulden erreicht'}
      </p>
    </div>`).join('');

  return `
    <div id="immo-debt-banner" class="immo-debt-banner">
      <p class="immo-debt-banner-title">IMMOBILIEN-ABRECHNUNG</p>
      ${items}
      <div class="immo-debt-footer">
        <span class="immo-debt-total">Gesamt: ${fmtSeur(String(totalDebt))}</span>
        <button class="immo-btn immo-btn-danger" data-action="scroll-to-businesses">JETZT BEZAHLEN</button>
      </div>
    </div>
  `;
}

// ─── Betriebe-Shop (kaufen + eigene verwalten) ────────────────────────────────

function renderBusinessShop(): string {
  const ALL_DEFS: LaundryBusinessDef[] = [
    { type:'KIOSK',        name:'Kiosk "Zur Letzten Hoffnung"',   description:'Niemand kauft hier nur Zigaretten.',             purchasePriceEurCents:'200000',   maintenancePerWeekCents:'20000',  maxBatchCents:'50000',   baseLossPercent:33, baseDurationMinutes:60,  maxUpgradeLevel:3, unlockLevel:1,  basePoliceRiskPercent:1  },
    { type:'IMBISS',       name:'Döner-Imbiss "Chefkoch"',        description:'Niemand fragt wie der Umsatz so hoch ist.',      purchasePriceEurCents:'500000',   maintenancePerWeekCents:'30000',  maxBatchCents:'200000',  baseLossPercent:28, baseDurationMinutes:120, maxUpgradeLevel:3, unlockLevel:10, basePoliceRiskPercent:2  },
    { type:'WASCHSALON',   name:'Waschsalon "Blütenrein"',        description:'Acht Maschinen. Drei echte Kunden.',             purchasePriceEurCents:'1500000',  maintenancePerWeekCents:'40000',  maxBatchCents:'500000',  baseLossPercent:24, baseDurationMinutes:240, maxUpgradeLevel:3, unlockLevel:15, basePoliceRiskPercent:3  },
    { type:'TATTOO_STUDIO',name:'Tattoo-Studio "Tinte & Schweigen"',description:'Keine Namen. Keine Fotos. Bargeldzahlung.',   purchasePriceEurCents:'2500000',  maintenancePerWeekCents:'60000',  maxBatchCents:'500000',  baseLossPercent:22, baseDurationMinutes:180, maxUpgradeLevel:3, unlockLevel:15, basePoliceRiskPercent:4  },
    { type:'AUTOHANDEL',   name:'Gebrauchtwagen Meier & Sohn',    description:'Alle Autos haben TÜV.',                         purchasePriceEurCents:'4500000',  maintenancePerWeekCents:'90000',  maxBatchCents:'1200000', baseLossPercent:20, baseDurationMinutes:480, maxUpgradeLevel:3, unlockLevel:20, basePoliceRiskPercent:5  },
    { type:'NACHTCLUB',    name:'Club "Schwarzlicht"',             description:'Der Türsteher kennt keine Gesichter.',          purchasePriceEurCents:'15000000', maintenancePerWeekCents:'280000', maxBatchCents:'3500000', baseLossPercent:16, baseDurationMinutes:1440,maxUpgradeLevel:3, unlockLevel:30, basePoliceRiskPercent:8  },
    { type:'SPIELHALLE',   name:'Spielhalle "Lucky Strike"',      description:'Jackpot-Quote: 0,1 %.',                         purchasePriceEurCents:'35000000', maintenancePerWeekCents:'600000', maxBatchCents:'8000000', baseLossPercent:14, baseDurationMinutes:2880,maxUpgradeLevel:3, unlockLevel:40, basePoliceRiskPercent:12 },
  ];

  const ownedMap = new Map(businesses.map((b) => [b.type, b]));
  const playerLevel = gameState.getPlayer()?.level ?? 1;

  const rows = ALL_DEFS.map((d) => {
    const owned = ownedMap.get(d.type);

    if (owned) {
      // ── Eigener Betrieb ───────────────────────────────────────────────────
      const statusColor  = owned.status === 'ACTIVE' ? 'var(--accent-green)' : owned.status === 'CREDIT' ? 'var(--accent-orange)' : 'var(--accent-red)';
      const statusLabel  = owned.status === 'ACTIVE' ? 'AKTIV' : owned.status === 'CREDIT' ? 'KREDIT' : 'EINGEFROREN';
      const hasDebt      = parseInt(owned.maintenanceDebt, 10) > 0;
      const canUpgrade   = owned.upgrade.available && owned.status !== 'FROZEN' && owned.activeRun === null;
      const upgradeLabel = owned.upgrade.available && owned.upgrade.costEurCents
        ? `UPGRADE – ${fmtEur(owned.upgrade.costEurCents)}`
        : 'MAX. STUFE';
      const maintenanceDue = new Date(owned.nextMaintenanceDueAt);
      const hoursUntilDue  = Math.ceil((maintenanceDue.getTime() - Date.now()) / 3_600_000);

      return `
        <div class="immo-biz-card">
          <div class="immo-biz-header">
            <span class="immo-biz-name">${owned.name}</span>
            <span class="immo-biz-status-badge" style="color:${statusColor};">● ${statusLabel} &nbsp;STUFE ${String(owned.upgradeLevel)}</span>
          </div>
          <div class="immo-biz-meta">
            <span class="immo-biz-meta-item">Unterhalt: <span style="color:var(--accent-orange);">${fmtSeur(owned.maintenance.perWeekCents)}/Woche</span></span>
            <span class="immo-biz-meta-item">Nächste Zahlung: <span style="color:${hoursUntilDue > 0 ? 'var(--text-muted)' : 'var(--accent-red)'};">${hoursUntilDue > 0 ? `${String(hoursUntilDue)}h` : 'FÄLLIG'}</span></span>
          </div>
          ${hasDebt ? `
            <div class="immo-biz-debt-box" style="border-color:${statusColor};background:color-mix(in srgb,${statusColor} 6%,transparent);">
              <p class="immo-biz-debt-label" style="color:${statusColor};">SCHULDEN: ${owned.maintenanceDebtDisplay}</p>
              <div class="immo-row immo-btn-row">
                ${owned.maintenance.canPayWithBlackMoney
                  ? `<button class="immo-btn immo-btn-warn" data-action="pay-debt" data-id="${owned.id}" data-eur="false">S-₦ ZAHLEN – ${owned.maintenanceDebtDisplay}</button>`
                  : ''}
                <button class="immo-btn immo-btn-muted" data-action="pay-debt" data-id="${owned.id}" data-eur="true">₦ ZAHLEN – ${fmtEur(owned.maintenance.eurAlternativeCents)} (1,5×)</button>
              </div>
            </div>
          ` : ''}
          <div class="immo-row immo-btn-row">
            <button class="immo-btn immo-btn-muted" data-action="upgrade" data-id="${owned.id}" ${!canUpgrade ? 'disabled' : ''}>${upgradeLabel}</button>
            <button class="immo-btn immo-btn-danger" data-action="sell" data-id="${owned.id}" ${owned.sale.hasActiveRun ? 'disabled title="Aktiver Lauf – erst in Crime Ops einsammeln"' : ''}>VERKAUFEN – ${fmtEur(owned.sale.valueEurCents)}</button>
          </div>
        </div>
      `;
    }

    // ── Nicht gekaufter Betrieb ───────────────────────────────────────────
    const locked       = playerLevel < d.unlockLevel || businesses.length >= 2;
    const lockedReason = businesses.length >= 2 ? 'MAX. BETRIEBE' : `LVL ${String(d.unlockLevel)}`;
    return `
      <div class="immo-shop-item">
        <div class="immo-shop-info">
          <span class="immo-shop-name" style="color:${locked ? 'var(--text-muted)' : 'var(--text-primary)'};">${d.name}</span>
          <span class="immo-shop-lore">${d.description}</span>
          <div class="immo-shop-stats">
            <span class="immo-shop-stat">Verlust: ${String(d.baseLossPercent)}%</span>
            <span class="immo-shop-stat">Unterhalt: ${fmtSeur(d.maintenancePerWeekCents)}/Woche</span>
            <span class="immo-shop-stat">Risiko: ${String(d.basePoliceRiskPercent)}%</span>
          </div>
        </div>
        <div class="immo-shop-actions">
          <span class="immo-shop-price">${fmtEur(d.purchasePriceEurCents)}</span>
          <button class="immo-btn ${locked ? 'immo-btn-muted' : 'immo-btn-primary'}" data-action="buy" data-type="${d.type}" ${locked ? 'disabled' : ''}>
            ${locked ? `[${lockedReason}]` : 'KAUFEN'}
          </button>
        </div>
      </div>
    `;
  }).join('');

  return `
    <div>
      <h3 class="prop-section-title">BETRIEBE (${String(businesses.length)}/2 IM BESITZ)</h3>
      ${rows}
    </div>
  `;
}

// ─── Event-Delegation ─────────────────────────────────────────────────────────

function attachContentListeners(): void {
  const content = document.getElementById('immo-content');
  if (!content) return;

  content.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-action]');
    if (!btn || btn.hasAttribute('disabled')) return;
    const action = btn.dataset.action ?? '';
    const id = btn.dataset.id ?? '';
    const type = btn.dataset.type ?? '';

    switch (action) {
      case 'scroll-to-businesses':
        document.querySelector('[data-action="pay-debt"]')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'start-wash':
        showWaescheModal(id);
        break;
      case 'collect':
        void handleCollect(id, btn.dataset.runid ?? '');
        break;
      case 'upgrade':
        void handleUpgrade(id);
        break;
      case 'sell':
        showVerkaufModal(id);
        break;
      case 'buy':
        void handleBuy(type);
        break;
      case 'pay-debt':
        void handlePayDebt(id, btn.dataset.eur === 'true');
        break;
    }
  });
}

// ─── Aktions-Handler ─────────────────────────────────────────────────────────

async function handleCollect(_businessId: string, runId: string): Promise<void> {
  try {
    await api.post<unknown>(`/laundry/runs/${runId}/collect`, {});
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await loadData();
  } catch (err) {
    alert(err instanceof ApiError ? err.message : 'Fehler beim Einsammeln.');
  }
}

async function handleUpgrade(businessId: string): Promise<void> {
  try {
    await api.post<unknown>(`/laundry/businesses/${businessId}/upgrade`, {});
    void refreshBalance();
    await loadData();
  } catch (err) {
    alert(err instanceof ApiError ? err.message : 'Fehler beim Upgrade.');
  }
}

async function handleBuy(type: string): Promise<void> {
  try {
    await api.post<unknown>('/laundry/businesses/purchase', { type });
    void refreshBalance();
    await loadData();
  } catch (err) {
    alert(err instanceof ApiError ? err.message : 'Kauf fehlgeschlagen.');
  }
}

async function handlePayDebt(businessId: string, payWithEur: boolean): Promise<void> {
  try {
    await api.post<unknown>(`/laundry/businesses/${businessId}/debt/pay`, { payWithEur });
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await loadData();
  } catch (err) {
    alert(err instanceof ApiError ? err.message : 'Zahlung fehlgeschlagen.');
  }
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function openModal(html: string): void {
  const overlay = document.getElementById('immo-modal-overlay');
  const modal = document.getElementById('immo-modal');
  if (!overlay || !modal) return;
  modal.innerHTML = html;
  overlay.style.display = '';
  overlay.onclick = (e) => { if (e.target === overlay) closeModal(); };
}

function closeModal(): void {
  const overlay = document.getElementById('immo-modal-overlay');
  const modal = document.getElementById('immo-modal');
  if (overlay) overlay.style.display = 'none';
  if (modal) modal.innerHTML = '';
}

function showWaescheModal(businessId: string): void {
  const b = businesses.find((x) => x.id === businessId);
  if (!b) return;

  const playerBm = parseInt(gameState.getPlayer()?.blackMoney ?? '0', 10);
  const maxBatch = parseInt(b.effective.maxBatchCents, 10);
  const usableMax = Math.min(playerBm, maxBatch);
  const step = 100; // 1 S-EUR Schritte
  const initialVal = Math.floor(usableMax / 2 / step) * step;

  openModal(`
    <div class="modal-header">
      <h2>WÄSCHE STARTEN</h2>
      <p class="immo-modal-subtitle">${b.name} · Stufe ${String(b.upgradeLevel)}</p>
    </div>
    <div class="modal-body">
      <div class="immo-modal-kv">
        <span class="immo-modal-kv-key">Verfügbares S-₦:</span>
        <span class="immo-modal-kv-val" style="color:var(--accent-bm);">${fmtSeur(String(playerBm))}</span>
      </div>
      <div class="immo-modal-kv" style="margin-bottom:var(--space-3);">
        <span class="immo-modal-kv-key">Max. Batch:</span>
        <span class="immo-modal-kv-val">${fmtSeur(b.effective.maxBatchCents)}</span>
      </div>

      <label class="immo-modal-label">BETRAG (S-₦)</label>
      <input type="range" id="wasche-slider"
        min="${String(step)}" max="${String(usableMax)}" step="${String(step)}" value="${String(initialVal)}"
        style="width:100%;accent-color:var(--accent-bm);margin-bottom:var(--space-1);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-3);">
        <button class="immo-btn immo-btn-muted" style="min-height:36px;padding:var(--space-1) var(--space-3);" onclick="document.getElementById('wasche-slider').value='${String(step)}';updateWaschePreview()">MIN</button>
        <span id="wasche-amount-display" class="immo-modal-amount">${fmtSeur(String(initialVal))}</span>
        <button class="immo-btn immo-btn-muted" style="min-height:36px;padding:var(--space-1) var(--space-3);" onclick="document.getElementById('wasche-slider').value='${String(usableMax)}';updateWaschePreview()">MAX</button>
      </div>

      <div id="wasche-preview" class="immo-modal-preview">
        <div class="immo-modal-kv">
          <span class="immo-modal-kv-key">Verlust (${String(b.effective.lossPercent)}%):</span>
          <span id="wasche-loss" class="immo-modal-kv-val" style="color:var(--accent-red);"></span>
        </div>
        <div class="immo-modal-kv">
          <span class="immo-modal-kv-key">₦-Output:</span>
          <span id="wasche-output" class="immo-modal-kv-val" style="color:var(--accent-green);"></span>
        </div>
        <div class="immo-modal-kv">
          <span class="immo-modal-kv-key">Dauer:</span>
          <span class="immo-modal-kv-val">${fmtDuration(b.effective.durationMinutes)}</span>
        </div>
      </div>
    </div>
    <div class="modal-actions" style="flex-wrap:wrap;">
      <button class="immo-btn immo-btn-primary" id="wasche-start-btn" style="flex:1;min-width:140px;">WÄSCHE STARTEN</button>
      <button class="immo-btn immo-btn-muted" onclick="window._immoCloseModal()" style="flex:1;min-width:140px;">ABBRECHEN</button>
    </div>
  `);

  (window as unknown as Record<string, unknown>)._immoCloseModal = closeModal;

  // Slider-Preview aktualisieren
  (window as unknown as Record<string, unknown>).updateWaschePreview = () => { updateWaschePreview(b); };
  const slider = document.getElementById('wasche-slider') as HTMLInputElement | null;
  if (slider) slider.oninput = () => { updateWaschePreview(b); };
  updateWaschePreview(b);

  // Start-Button
  const startBtn = document.getElementById('wasche-start-btn');
  if (startBtn) {
    startBtn.onclick = async () => {
      const sliderEl = document.getElementById('wasche-slider') as HTMLInputElement | null;
      const amount = parseInt(sliderEl?.value ?? '0', 10);
      if (amount < 100) return;
      startBtn.setAttribute('disabled', '');
      startBtn.textContent = 'STARTET...';
      try {
        await api.post<unknown>(`/laundry/businesses/${businessId}/runs/start`, {
          blackMoneyAmountCents: String(amount),
        });
        closeModal();
        void refreshBalance(); // NT-HOTFIX-01 (H6)
        await loadData();
      } catch (err) {
        startBtn.removeAttribute('disabled');
        startBtn.textContent = 'WÄSCHE STARTEN';
        alert(err instanceof ApiError ? err.message : 'Start fehlgeschlagen.');
      }
    };
  }
}

function updateWaschePreview(b: LaundryBusinessState): void {
  const slider = document.getElementById('wasche-slider') as HTMLInputElement | null;
  if (!slider) return;
  const amount = parseInt(slider.value, 10);
  const lossPercent = b.effective.lossPercent;
  const loss = Math.floor((amount * lossPercent) / 100);
  const output = amount - loss;

  const amountDisplay = document.getElementById('wasche-amount-display');
  const lossDisplay = document.getElementById('wasche-loss');
  const outputDisplay = document.getElementById('wasche-output');
  if (amountDisplay) amountDisplay.textContent = fmtSeur(String(amount));
  if (lossDisplay) lossDisplay.textContent = `- ${fmtSeur(String(loss))}`;
  if (outputDisplay) outputDisplay.textContent = `+ ${fmtEur(String(output))}`;
}

function showVerkaufModal(businessId: string): void {
  const b = businesses.find((x) => x.id === businessId);
  if (!b) return;

  openModal(`
    <div class="modal-header">
      <h2>BETRIEB VERKAUFEN</h2>
      <p class="immo-modal-subtitle">${b.name} · Stufe ${String(b.upgradeLevel)}</p>
    </div>
    <div class="modal-body">
      <div class="immo-modal-kv">
        <span class="immo-modal-kv-key">Rückerstattung (40%):</span>
        <span class="immo-modal-kv-val" style="color:var(--accent-green);">+ ${fmtEur(b.sale.valueEurCents)}</span>
      </div>
      <div class="immo-modal-kv">
        <span class="immo-modal-kv-key">Upgrades (Stufe ${String(b.upgradeLevel)}):</span>
        <span class="immo-modal-kv-val">verfallen</span>
      </div>
      ${parseInt(b.maintenanceDebt, 10) > 0 ? `
        <div class="immo-modal-kv">
          <span class="immo-modal-kv-key">Offene Schulden:</span>
          <span class="immo-modal-kv-val" style="color:var(--accent-green);">erlassen</span>
        </div>` : ''}
      <p class="immo-modal-warn">Diese Aktion kann nicht rückgängig gemacht werden.</p>
    </div>
    <div class="modal-actions">
      <button class="immo-btn immo-btn-muted" onclick="window._immoCloseModal()">ABBRECHEN</button>
      <button class="immo-btn immo-btn-danger" id="sell-confirm-btn" data-id="${businessId}">JETZT VERKAUFEN</button>
    </div>
  `);

  (window as unknown as Record<string, unknown>)._immoCloseModal = closeModal;

  const confirmBtn = document.getElementById('sell-confirm-btn');
  if (confirmBtn) {
    confirmBtn.onclick = async () => {
      confirmBtn.setAttribute('disabled', '');
      confirmBtn.textContent = 'VERKAUFE...';
      try {
        await api.post<unknown>(`/laundry/businesses/${businessId}/sell`, {});
        closeModal();
        void refreshBalance(); // NT-HOTFIX-01 (H6)
        await loadData();
      } catch (err) {
        confirmBtn.removeAttribute('disabled');
        confirmBtn.textContent = 'JETZT VERKAUFEN';
        alert(err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.');
      }
    };
  }
}

// ─── Progress-Bar Live-Update ─────────────────────────────────────────────────

function updateProgressBars(): void {
  for (const b of businesses) {
    const run = b.activeRun;
    if (run?.status !== 'RUNNING') continue;

    const pct = calcProgress(run);
    const fillEl = document.getElementById(`immo-prog-${b.id}`);
    const pctEl = document.getElementById(`immo-pct-${b.id}`);
    const timeEl = document.getElementById(`immo-timeleft-${b.id}`);

    if (fillEl) fillEl.style.width = `${String(pct)}%`;
    if (pctEl) pctEl.textContent = `${String(pct)}%`;
    if (timeEl) timeEl.textContent = timeLeft(run.completesAt);

    // Einsammeln-Button einblenden wenn fertig
    if (pct >= 100) {
      void loadData();
      break;
    }
  }
}
